import { formatSpan, toLocalSpan, type IsoDate } from '@mai-team-amusement/core';
import { MINUTES_PER_HOUR, type ScheduleDay, type WorkDayView } from '@mai-work-clock/contracts';
import { DateTime } from 'luxon';

const MINUTE_DIGITS = 2;
const TIME_FORMAT = 'HH:mm';
const MONTH_FORMAT = 'yyyy-MM';
const MONTH_LENGTH = 7;
const MAP_ZOOM = 17;

/** Month steps for previous / next navigation. */
export const MonthStep = {
  Previous: -1,
  Next: 1,
} as const;

/** `YYYY-MM` of a `YYYY-MM-DD` date. */
export const monthOfDate = (date: IsoDate) => date.slice(0, MONTH_LENGTH);

/** Minutes as `H:MM`. */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  return `${String(hours)}:${String(minutes % MINUTES_PER_HOUR).padStart(MINUTE_DIGITS, '0')}`;
}

/** Minutes after midnight as `HH:mm`. */
export function formatMinuteOfDay(minuteOfDay: number): string {
  return DateTime.fromObject({
    hour: Math.floor(minuteOfDay / MINUTES_PER_HOUR),
    minute: minuteOfDay % MINUTES_PER_HOUR,
  }).toFormat(TIME_FORMAT);
}

export function minuteOfDay(time: DateTime): number {
  return time.hour * MINUTES_PER_HOUR + time.minute;
}

/** `09:00-18:00`, `22:00-06:00(+1)`. */
export function formatScheduleSpan(day: ScheduleDay): string {
  const overnight = day.endMinute <= day.startMinute ? '(+1)' : '';
  return `${formatMinuteOfDay(day.startMinute)}-${formatMinuteOfDay(day.endMinute)}${overnight}`;
}

/** `09:02-18:31`, `22:00-06:30(+1)` or `09:02-` while open, in the day's own time zone. */
export function formatWorkSpan(
  day: Pick<WorkDayView, 'workDate' | 'startAt' | 'endAt' | 'timeZone'>,
) {
  return formatSpan(
    toLocalSpan(
      day.workDate,
      new Date(day.startAt),
      day.endAt === null ? null : new Date(day.endAt),
      day.timeZone,
    ),
  );
}

export function formatTime(iso: string, timeZone: string): string {
  return DateTime.fromISO(iso, { zone: timeZone }).toFormat(TIME_FORMAT);
}

export function formatDateTime(iso: string, timeZone: string, locale: string): string {
  return DateTime.fromISO(iso, { zone: timeZone })
    .setLocale(locale)
    .toLocaleString(DateTime.DATETIME_MED);
}

/** e.g. `10/12 (Mon)` / `10/12(月)`, in the UI language. */
export function formatDayLabel(date: IsoDate, locale: string): string {
  return DateTime.fromISO(date)
    .setLocale(locale)
    .toLocaleString({ month: 'numeric', day: 'numeric', weekday: 'short' });
}

export function formatLongDate(date: IsoDate, locale: string): string {
  return DateTime.fromISO(date)
    .setLocale(locale)
    .toLocaleString({ year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
}

export function formatMonth(month: string, locale: string): string {
  return DateTime.fromISO(`${month}-01`)
    .setLocale(locale)
    .toLocaleString({ year: 'numeric', month: 'long' });
}

export function monthOf(instant: Date, timeZone: string): string {
  return DateTime.fromJSDate(instant, { zone: timeZone }).toFormat(MONTH_FORMAT);
}

export function shiftMonth(month: string, months: number): string {
  return DateTime.fromISO(`${month}-01`).plus({ months }).toFormat(MONTH_FORMAT);
}

/** OpenStreetMap link to a recorded position. */
export function mapUrl(latitude: number, longitude: number): string {
  return `https://www.openstreetmap.org/?mlat=${String(latitude)}&mlon=${String(longitude)}#map=${String(MAP_ZOOM)}/${String(latitude)}/${String(longitude)}`;
}
